/**
 * Topics Page
 * Browse courses by topic category
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { coursesService, TopicInfo } from '../services/coursesService';
import { useSeoTags, buildWebPageJsonLd } from '../hooks/useSeoTags';
import './TopicsPage.css';

// Visual config per topic — color + SVG icon path
const TOPIC_CONFIG: Record<string, { color: string; icon: string }> = {
  'vocab.daily_life': {
    color: '#6366f1',
    // house
    icon: 'M3 10.5L12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  },
  'vocab.people': {
    color: '#8b5cf6',
    // two people
    icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm13 10v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  },
  'vocab.food_drink': {
    color: '#f59e0b',
    // coffee cup
    icon: 'M18 8h1a3 3 0 0 1 0 6h-1M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8zm0-4h16',
  },
  'vocab.shopping_money': {
    color: '#10b981',
    // shopping bag
    icon: 'M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4H6zm0 4h12M16 10a4 4 0 0 1-8 0',
  },
  'vocab.travel_transport': {
    color: '#3b82f6',
    // compass
    icon: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-2.5 12.5L7 7l8.5 2.5L18 17z',
  },
  'vocab.health_body': {
    color: '#ef4444',
    // heart
    icon: 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z',
  },
  'vocab.education': {
    color: '#6366f1',
    // graduation cap
    icon: 'M2 10l10-5 10 5-10 5zm4 3v4l6 3 6-3v-4',
  },
  'vocab.work': {
    color: '#0891b2',
    // briefcase
    icon: 'M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2',
  },
  'vocab.nature_environment': {
    color: '#22c55e',
    // mountain + sun
    icon: 'M3 20l5-8 4 4 5-10 4 14H3zM5.5 7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  },
  'vocab.entertainment': {
    color: '#ec4899',
    // music note
    icon: 'M9 18V5l12-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zm12-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0z',
  },
  'vocab.technology': {
    color: '#6b7280',
    // monitor
    icon: 'M2 4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4zm6 16h8m-4-2v2',
  },
  'vocab.society': {
    color: '#f97316',
    // globe
    icon: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM2 12h20M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10A15 15 0 0 1 12 2z',
  },
  'vocab.science': {
    color: '#14b8a6',
    // flask
    icon: 'M9 3h6m-5 0v6.5L4 18a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1l-6-8.5V3m-4.5 12h7',
  },
};

const TopicIcon: React.FC<{ code: string; color: string }> = ({ code, color }) => {
  const config = TOPIC_CONFIG[code];
  const d = config?.icon || 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z';
  return (
    <svg
      className="topic-icon-svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={d} />
    </svg>
  );
};

const TopicsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['course', 'common']);
  const [topics, setTopics] = useState<TopicInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTopics();
  }, []);

  const loadTopics = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await coursesService.getTopics();
      setTopics(data);
    } catch (err: any) {
      console.error('Failed to load topics:', err);
      setError(err.message || 'Failed to load topics');
    } finally {
      setLoading(false);
    }
  };

  const handleTopicClick = (topicCode: string) => {
    navigate(`/courses?topic=${topicCode}`);
  };

  const getTopicLabel = (topic: TopicInfo): string => {
    return topic.labels[i18n.language] || topic.labels['en'] || topic.code;
  };

  const getTopicColor = (code: string): string => {
    return TOPIC_CONFIG[code]?.color || '#667eea';
  };

  // SEO for /topics — discovery hub. WebPage + breadcrumbs only; the actual
  // topic list isn't a schema.org-friendly shape (it's a flat tag list, not
  // a ranked ItemList of products/courses).
  const topicsTitle = t('course:topics.title', { defaultValue: 'Browse by Topic' });
  const topicsSubtitle = t('course:topics.subtitle', {
    defaultValue: 'Explore English learning courses organized by everyday topics.',
  });
  const seoTags = useSeoTags({
    title: topicsTitle,
    description: topicsSubtitle,
    canonical: '/topics',
    jsonLd: buildWebPageJsonLd({
      url: '/topics',
      name: topicsTitle,
      description: topicsSubtitle,
      breadcrumbs: [
        { name: 'Loopy', url: '/' },
        { name: topicsTitle, url: '/topics' },
      ],
    }),
  });

  if (loading) {
    return (
      <div className="topics-page">
        {seoTags}
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t('common:loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="topics-page">
      {seoTags}
      <div className="topics-header">
        <h1>{t('course:topics.title')}</h1>
        <p className="topics-subtitle">{t('course:topics.subtitle')}</p>
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={loadTopics}>{t('common:retry')}</button>
        </div>
      )}

      <div className="topics-grid">
        {topics.map((topic) => {
          const color = getTopicColor(topic.code);
          return (
            <button
              key={topic.code}
              className="topic-card"
              onClick={() => handleTopicClick(topic.code)}
              style={{
                '--topic-color': color,
                '--topic-bg': `${color}0a`,
                '--topic-bg-hover': `${color}14`,
              } as React.CSSProperties}
            >
              <div className="topic-accent" />
              <div className="topic-icon-wrapper">
                <TopicIcon code={topic.code} color={color} />
              </div>
              <h3 className="topic-name">{getTopicLabel(topic)}</h3>
              <span className="topic-count-label">
                {t('course:topics.courseCount', { count: topic.courseCount })}
              </span>
            </button>
          );
        })}
      </div>

      {topics.length === 0 && !error && (
        <div className="empty-state">
          <h3>{t('course:topics.noTopics')}</h3>
          <p>{t('course:topics.noTopicsDesc')}</p>
        </div>
      )}
    </div>
  );
};

export default TopicsPage;
